import React, { useState, useMemo, forwardRef, useImperativeHandle } from 'react';
import DataPrivacyPromise from './DataPrivacyPromise';
import SectionHeader from './SectionHeader';
import { fertilityDemoEditButton, FERTILITY_DEMO_SECTION_BORDER } from '../designSystem';

function uid(prefix = 'cyc') {
  return `${prefix}_${Math.random().toString(16).slice(2)}_${Date.now().toString(16)}`;
}

function formatMonthYear(dateStr) {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

const OUTCOME_PILLS = {
  current: { text: 'Current cycle', bg: 'rgba(212,83,126,0.08)', color: '#D4537E', border: 'rgba(212,83,126,0.25)', dot: '#D4537E' },
  pregnant: { text: 'Got pregnant', bg: '#E1F5EE', color: '#0F6E56', border: 'rgba(29,158,117,0.3)', dot: '#1D9E75' },
  frozen: { text: 'Embryos frozen', bg: '#E6F1FB', color: '#0C447C', border: 'rgba(24,95,165,0.25)', dot: '#185FA5' },
  eggs: { text: 'Eggs frozen', bg: '#EEEDFE', color: '#534AB7', border: 'rgba(127,119,221,0.25)', dot: '#7F77DD' },
  'didnt-work': { text: "Transfer didn't work", bg: '#FBEAF0', color: '#993556', border: 'rgba(212,83,126,0.2)', dot: '#D4537E' },
  cancelled: { text: 'Cycle cancelled', bg: 'var(--color-background-secondary, #f5f5f5)', color: 'var(--color-text-tertiary, #888)', border: 'var(--color-border-secondary, #e0e0e0)', dot: 'var(--color-text-tertiary, #888)' },
  waiting: { text: 'Still waiting', bg: '#FBEAF0', color: '#D4537E', border: 'rgba(212,83,126,0.2)', dot: '#D4537E' },
};

function getSummaryLine(cycle) {
  if (cycle.cancelled) return `Cancelled — ${cycle.cancelReason || 'Unknown reason'}`;
  if (cycle.outcome === 'current') return `${cycle.protocol || '—'} · stimming · est. retrieval ${cycle.retrievalDate || '—'}`;
  if (cycle.path === 'eggs') return `${cycle.eggsRetrieved || 0} eggs retrieved · frozen for future use`;
  if (cycle.path === 'embryos') return `${cycle.eggsRetrieved || 0} retrieved · ${cycle.fertilized || 0} fertilized · ${cycle.blastocysts || 0} embryos frozen`;
  if (cycle.path === 'transfer') return `${cycle.embryoTransferred || '1'} embryo transferred · ${cycle.embryoGrades || '—'} · ${cycle.outcome === 'pregnant' ? 'Positive' : cycle.outcome === 'didnt-work' ? "Didn't implant" : '—'}`;
  return `${cycle.protocol || '—'} · ${cycle.retrievalDate || '—'}`;
}

function getCycleName(cycle, index) {
  if (cycle.path === 'transfer' && cycle.transferType === 'frozen') return `Cycle ${cycle.cycleNumber} — frozen transfer`;
  return `Cycle ${cycle.cycleNumber}`;
}

const PROTOCOL_OPTIONS = ['Antagonist', 'Lupron long', 'Mini IVF', 'Medicated FET', 'Natural FET', 'Other'];
const PGT_OPTIONS = ['No — not testing', 'Yes — sent for PGT', 'Yes — results back'];
const TRANSFER_OPTIONS = [
  { label: 'Fresh transfer (this retrieval cycle)', value: 'fresh' },
  { label: 'Frozen transfer (FET) — from a previous cycle', value: 'frozen' },
  { label: 'No transfer this cycle — banking embryos', value: 'none' },
];
const CANCEL_OPTIONS = ['No — cycle completed', 'Yes — poor response to stims', 'Yes — medical reason', 'Yes — personal decision', 'Yes — other reason'];

function mapContextCycleToModel(c, index) {
  const cycleNum = (c.cycleNumber != null) ? c.cycleNumber : (index + 1);
  const startDate = c.startedAt || c.startDate;
  return {
    id: c.id || uid(),
    cycleNumber: cycleNum,
    startDate: startDate ? new Date(startDate).toLocaleDateString('en-US') : '',
    protocol: c.protocol || '',
    clinic: c.clinic || '',
    doctor: c.doctor || '',
    path: c.path || 'embryos',
    retrievalDate: c.retrievalDate || c.estimatedRetrieval || '',
    estimatedRetrieval: c.estimatedRetrieval || c.retrievalDate || '',
    follicleCount: c.follicleCount ?? c.lastScan?.follicleCount ?? null,
    eggsRetrieved: c.eggsRetrieved ?? 0,
    matureEggs: c.matureEggs ?? 0,
    stimMedications: c.stimMedications || '',
    fertilized: c.fertilized ?? 0,
    blastocysts: c.blastocysts ?? 0,
    embryoGrades: c.embryoGrades || '',
    pgtTesting: c.pgtTesting || 'no',
    pgtResults: c.pgtResults || '',
    transferType: c.transferType || 'none',
    transferDate: c.transferDate || '',
    embryoTransferred: c.embryoTransferred || '',
    outcome: c.outcome || (c.id ? 'waiting' : 'current'),
    hcgLevel: c.hcgLevel || '',
    cancelled: c.cancelled || false,
    cancelReason: c.cancelReason || '',
    notes: c.notes || '',
    name: c.name || `Cycle ${cycleNum}`,
    startedAt: c.startedAt || startDate,
  };
}

const INPUT_STYLE = {
  fontSize: 11,
  fontFamily: 'inherit',
  background: 'var(--color-background-secondary, #f8f8f8)',
  border: '0.5px solid var(--color-border-secondary, #e0e0e0)',
  borderRadius: 6,
  padding: '7px 10px',
  color: '#2d1a22',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
};
const LABEL_STYLE = { fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 4, display: 'block', fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif" };

const YourCyclesCard = forwardRef(function YourCyclesCard(
  { journey, addCycle, updateCycle, setActiveCycleId, showIVF, showTTC, isDemoFertility = false },
  ref,
) {
  const contextCycles = journey?.cycles || [];
  const initialCycles = useMemo(() => {
    if (contextCycles.length > 0) {
      return contextCycles.map((c, i) => mapContextCycleToModel({ ...c }, i));
    }
    return [
      mapContextCycleToModel({
        id: uid(),
        cycleNumber: 2,
        startDate: '2026-03-13',
        protocol: 'Antagonist',
        path: 'embryos',
        outcome: 'current',
        eggsRetrieved: 12,
        fertilized: 8,
        blastocysts: 5,
        name: 'Cycle 2',
      }, 0),
    ];
  }, []);

  const [cycles, setCycles] = useState(initialCycles);
  const [expandedId, setExpandedId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCycle, setEditingCycle] = useState(null);
  const [form, setForm] = useState({});

  const sortedCycles = useMemo(() => [...cycles].sort((a, b) => {
    const da = new Date(b.startedAt || b.startDate || 0);
    const db = new Date(a.startedAt || a.startDate || 0);
    return da - db;
  }), [cycles]);

  const totals = useMemo(() => {
    const nonCancelled = cycles.filter((c) => !c.cancelled);
    return {
      eggs: nonCancelled.reduce((s, c) => s + (c.eggsRetrieved || 0), 0),
      fertilized: cycles.filter((c) => ['embryos', 'transfer'].includes(c.path)).reduce((s, c) => s + (c.fertilized || 0), 0),
      embryos: cycles.filter((c) => ['embryos', 'transfer'].includes(c.path) || c.path === 'eggs').reduce((s, c) => s + (c.blastocysts || 0), 0),
    };
  }, [cycles]);

  const openNewModal = () => {
    const nextNum = Math.max(0, ...cycles.map((c) => c.cycleNumber)) + 1;
    setEditingCycle(null);
    setForm({
      cycleNumber: nextNum,
      startDate: '',
      protocol: '',
      clinic: '',
      doctor: '',
      path: 'embryos',
      retrievalDate: '',
      estimatedRetrieval: '',
      follicleCount: null,
      eggsRetrieved: 0,
      matureEggs: 0,
      stimMedications: '',
      fertilized: 0,
      blastocysts: 0,
      embryoGrades: '',
      pgtTesting: 'no',
      pgtResults: '',
      transferType: 'none',
      transferDate: '',
      embryoTransferred: '',
      outcome: 'waiting',
      hcgLevel: '',
      cancelled: false,
      cancelReason: '',
      notes: '',
    });
    setModalOpen(true);
  };

  const openEditModal = (cycle) => {
    setEditingCycle(cycle);
    const startForInput = cycle.startedAt ? (cycle.startedAt.includes('-') ? cycle.startedAt : new Date(cycle.startedAt).toISOString().slice(0, 10)) : '';
    setForm({
      ...cycle,
      startDate: startForInput,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingCycle(null);
  };

  const openEditModalForContextCycle = (ctxCycle) => {
    if (!ctxCycle) return;
    const idx = contextCycles.findIndex((c) => c.id === ctxCycle.id);
    const model = mapContextCycleToModel({ ...ctxCycle }, idx >= 0 ? idx : 0);
    openEditModal(model);
  };

  useImperativeHandle(ref, () => ({
    openNewCycle: openNewModal,
    openEditCycle: openEditModalForContextCycle,
  }), [contextCycles]);

  const saveCycle = () => {
    const startIso = form.startDate ? (form.startDate.includes('-') ? form.startDate : new Date(form.startDate).toISOString().slice(0, 10)) : new Date().toISOString().slice(0, 10);
    const toSave = {
      ...form,
      id: editingCycle?.id || uid(),
      cycleNumber: form.cycleNumber || cycles.length + 1,
      name: `Cycle ${form.cycleNumber || cycles.length + 1}`,
      startedAt: startIso,
      startDate: new Date(startIso).toLocaleDateString('en-US'),
    };
    if (editingCycle) {
      setCycles((prev) => prev.map((c) => (c.id === editingCycle.id ? toSave : c)));
      updateCycle?.(editingCycle.id, toSave);
    } else {
      const newId = toSave.id;
      addCycle?.({
        id: newId,
        name: toSave.name,
        startedAt: startIso,
        stage: 'ivf',
        protocol: form.protocol,
        clinic: form.clinic,
        retrievalDate: form.retrievalDate || form.estimatedRetrieval,
        estimatedRetrieval: form.estimatedRetrieval || form.retrievalDate,
        follicleCount: form.follicleCount,
      });
      setCycles((prev) => [{ ...toSave, id: newId }, ...prev]);
    }
    closeModal();
  };

  const pillStyle = (outcome) => {
    const p = OUTCOME_PILLS[outcome] || OUTCOME_PILLS.waiting;
    return {
      display: 'inline-flex',
      padding: '3px 10px',
      borderRadius: 999,
      fontSize: 10,
      fontWeight: 500,
      border: `0.5px solid ${p.border}`,
      whiteSpace: 'nowrap',
      background: p.bg,
      color: p.color,
    };
  };

  const dotColor = (outcome) => (OUTCOME_PILLS[outcome] || OUTCOME_PILLS.waiting).dot;

  if (!showIVF && !showTTC) return null;

  return (
    <>
    <div
      style={{
        background: 'var(--card)',
        border: isDemoFertility ? FERTILITY_DEMO_SECTION_BORDER : '0.5px solid var(--border)',
        borderRadius: 14,
        padding: '16px 20px',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 12,
          marginBottom: 12,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <SectionHeader title="Your cycles" descriptor="Your IVF cycle history and outcomes." />
        </div>
        <button
          type="button"
          onClick={openNewModal}
          style={{
            flexShrink: 0,
            background: 'rgba(255,255,255,0.85)',
            border: '0.5px solid rgba(212,83,126,0.3)',
            borderRadius: 999,
            padding: '5px 14px',
            fontSize: 11,
            fontWeight: 500,
            color: '#D4537E',
            cursor: 'pointer',
            fontFamily: 'inherit',
            marginTop: 2,
          }}
        >
          + Start new cycle
        </button>
      </div>
      <div style={{ background: 'var(--white)', border: '0.5px solid rgba(212, 83, 126, 0.15)', borderRadius: 14, overflow: 'hidden' }}>
        <div style={{ padding: '16px 18px' }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 10, fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif" }}>
            Cycle history
          </div>

          {sortedCycles.map((cycle, idx) => {
            const isExpanded = expandedId === cycle.id;
            const isLast = idx === sortedCycles.length - 1;
            return (
              <div key={cycle.id} style={{ display: 'flex', gap: 10, paddingBottom: 8 }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div style={{ width: 9, height: 9, borderRadius: '50%', background: dotColor(cycle.outcome), flexShrink: 0 }} />
                  {!isLast && <div style={{ width: 1.5, flex: 1, minHeight: 8, background: 'var(--color-border-tertiary, #e5e5e5)' }} />}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => setExpandedId(isExpanded ? null : cycle.id)}
                    onKeyDown={(e) => e.key === 'Enter' && setExpandedId(isExpanded ? null : cycle.id)}
                    style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap', cursor: 'pointer', userSelect: 'none' }}
                  >
                    <span style={{ fontSize: 12, fontWeight: 500, color: '#2d1a22' }}>{getCycleName(cycle, idx)}</span>
                    <span style={pillStyle(cycle.outcome)}>{(OUTCOME_PILLS[cycle.outcome] || OUTCOME_PILLS.waiting).text}</span>
                    <span style={{ fontSize: 10, color: 'var(--color-text-tertiary, #888)' }}>{formatMonthYear(cycle.startedAt || cycle.startDate)}</span>
                    <span style={{ marginLeft: 'auto', fontSize: 10, color: '#D4537E', fontWeight: 500 }}>{isExpanded ? 'collapse' : 'expand'}</span>
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--color-text-tertiary, #888)' }}>{getSummaryLine(cycle)}</div>
                  {isExpanded && (
                    <div style={{ marginTop: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
                        <button
                          type="button"
                          onClick={() => openEditModal(cycle)}
                          style={
                            isDemoFertility
                              ? fertilityDemoEditButton
                              : {
                                  fontSize: 10,
                                  color: '#D4537E',
                                  fontWeight: 500,
                                  cursor: 'pointer',
                                  background: 'none',
                                  border: 'none',
                                  padding: 0,
                                  fontFamily: 'inherit',
                                }
                          }
                        >
                          Edit this cycle
                        </button>
                      </div>
                      <DataGrid cycle={cycle} path={cycle.path} />
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          <div style={{ borderTop: '0.5px solid var(--color-border-tertiary, #e5e5e5)', marginTop: 12, paddingTop: 12 }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 10, fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif" }}>
              Across all cycles
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 7 }}>
              <div style={{ background: 'linear-gradient(140deg, rgb(251,234,240), rgb(225,245,238))', border: '0.5px solid rgba(212,83,126,0.12)', borderRadius: 10, padding: '9px 11px' }}>
                <div style={{ fontSize: 18, fontWeight: 500, color: '#2d1a22' }}>{totals.eggs}</div>
                <div style={{ fontSize: 9, color: 'rgba(60,30,45,0.5)' }}>Eggs retrieved</div>
              </div>
              <div style={{ background: 'linear-gradient(140deg, rgb(251,234,240), rgb(225,245,238))', border: '0.5px solid rgba(212,83,126,0.12)', borderRadius: 10, padding: '9px 11px' }}>
                <div style={{ fontSize: 18, fontWeight: 500, color: '#2d1a22' }}>{totals.fertilized}</div>
                <div style={{ fontSize: 9, color: 'rgba(60,30,45,0.5)' }}>Fertilized</div>
              </div>
              <div style={{ background: 'linear-gradient(140deg, rgb(251,234,240), rgb(225,245,238))', border: '0.5px solid rgba(212,83,126,0.12)', borderRadius: 10, padding: '9px 11px' }}>
                <div style={{ fontSize: 18, fontWeight: 500, color: '#185FA5' }}>{totals.embryos}</div>
                <div style={{ fontSize: 9, color: 'rgba(60,30,45,0.5)' }}>Embryos banked</div>
              </div>
            </div>
          </div>
          <DataPrivacyPromise />
        </div>
      </div>
    </div>

    {modalOpen && (
        <LogCycleModal
          form={form}
          setForm={setForm}
          editingCycle={editingCycle}
          onClose={closeModal}
          onSave={saveCycle}
          INPUT_STYLE={INPUT_STYLE}
          LABEL_STYLE={LABEL_STYLE}
        />
      )}
    </>
  );
});

export default YourCyclesCard;

function DataGrid({ cycle, path }) {
  const rows = [];
  rows.push(['Start date', cycle.startDate || (cycle.startedAt ? new Date(cycle.startedAt).toLocaleDateString('en-US') : '—')]);
  rows.push(['Protocol', cycle.protocol || '—']);
  rows.push(['Clinic', cycle.clinic || '—']);
  rows.push(['Doctor', cycle.doctor || '—']);
  rows.push(['Retrieval date', cycle.retrievalDate || cycle.estimatedRetrieval || '—']);
  rows.push(['Follicles (last scan)', cycle.follicleCount != null ? String(cycle.follicleCount) : '—']);
  rows.push(['Eggs retrieved', String(cycle.eggsRetrieved ?? 0)]);
  rows.push(['Mature eggs', String(cycle.matureEggs ?? 0)]);
  if (path === 'embryos' || path === 'transfer') {
    rows.push(['Fertilized', String(cycle.fertilized ?? '—')]);
    rows.push(['Blastocysts', String(cycle.blastocysts ?? '—')]);
    rows.push(['Embryo grades', cycle.embryoGrades || '—']);
    rows.push(['PGT testing', cycle.pgtTesting || '—']);
    if (cycle.pgtTesting === 'Yes — results back') rows.push(['PGT results', cycle.pgtResults || '—']);
  }
  if (path === 'transfer') {
    rows.push(['Transfer type', cycle.transferType || '—']);
    rows.push(['Transfer date', cycle.transferDate || '—']);
    rows.push(['Embryo transferred', cycle.embryoTransferred || '—']);
    rows.push(['Outcome', cycle.outcome || '—']);
    rows.push(['HCG level', cycle.hcgLevel || '—']);
  }
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3px 14px', background: 'var(--card)', borderRadius: 8, padding: '10px 12px', marginBottom: 7 }}>
      {rows.map(([label, value]) => (
        <React.Fragment key={label}>
          <div style={{ fontSize: 10, color: 'var(--color-text-tertiary, #888)', padding: '2px 0' }}>{label}</div>
          <div style={{ fontSize: 10, fontWeight: 500, color: '#2d1a22', padding: '2px 0' }}>{value}</div>
        </React.Fragment>
      ))}
    </div>
  );
}

function LogCycleModal({ form, setForm, editingCycle, onClose, onSave, INPUT_STYLE, LABEL_STYLE }) {
  const showFert = form.path === 'embryos' || form.path === 'transfer';
  const showTransfer = form.path === 'transfer';
  const showPgtResults = form.pgtTesting === 'Yes — results back';

  const pathCards = [
    { value: 'eggs', title: 'Freeze eggs only', sub: 'No fertilization — storing eggs for later' },
    { value: 'embryos', title: 'Make embryos', sub: 'Fertilize and freeze — transfer in a future cycle' },
    { value: 'transfer', title: 'Make embryos + transfer', sub: 'Fertilize, possibly test (PGT), then transfer fresh or frozen' },
  ];

  const outcomeCards = [
    { value: 'pregnant', title: 'Got pregnant', sub: 'Pregnancy confirmed', color: '#0F6E56' },
    { value: 'didnt-work', title: "Transfer didn't work", sub: "Embryo didn't implant", color: '#993556' },
    { value: 'waiting', title: 'Still waiting', sub: "Haven't had beta yet", color: '#D4537E' },
    { value: 'cancelled', title: 'Cycle cancelled', sub: 'Stopped before transfer', color: 'var(--color-text-secondary, #666)' },
  ];

  return (
    <>
      <style>{`@keyframes yourCyclesSlideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }`}</style>
      <div style={{ minHeight: 500, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', position: 'fixed', inset: 0, zIndex: 1000 }}>
        <div
          style={{
            background: 'var(--white)',
            borderRadius: '16px 16px 0 0',
          width: '100%',
          maxWidth: 480,
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          animation: 'yourCyclesSlideUp 0.22s ease',
        }}
      >
        <div style={{ background: 'linear-gradient(140deg, rgb(251,234,240), rgb(225,245,238))', padding: '14px 18px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 500, color: '#2d1a22' }}>
              {editingCycle ? `Edit cycle ${form.cycleNumber}` : `Log cycle ${form.cycleNumber}`}
            </div>
            <div style={{ fontSize: 10, color: '#993556', marginTop: 2 }}>Fill in what you know — add more as the cycle progresses</div>
          </div>
          <button type="button" onClick={onClose} style={{ width: 26, height: 26, borderRadius: '50%', background: 'rgba(255,255,255,0.7)', border: 'none', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>×</button>
        </div>
        <div style={{ overflowY: 'auto', maxHeight: '62vh', padding: '16px 18px' }}>
          <section>
            <div style={LABEL_STYLE}>The basics</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div><label style={LABEL_STYLE}>Start date</label><input type="date" value={form.startDate?.includes('-') ? form.startDate : (form.startedAt?.includes('-') ? form.startedAt : '')} onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value }))} style={INPUT_STYLE} /></div>
              <div><label style={LABEL_STYLE}>Protocol</label><select value={form.protocol} onChange={(e) => setForm((f) => ({ ...f, protocol: e.target.value }))} style={INPUT_STYLE}><option value="">Select</option>{PROTOCOL_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}</select></div>
              <div style={{ gridColumn: '1 / -1' }}><label style={LABEL_STYLE}>Clinic</label><input value={form.clinic} onChange={(e) => setForm((f) => ({ ...f, clinic: e.target.value }))} style={INPUT_STYLE} placeholder="Clinic name" /></div>
              <div style={{ gridColumn: '1 / -1' }}><label style={LABEL_STYLE}>Doctor</label><input value={form.doctor} onChange={(e) => setForm((f) => ({ ...f, doctor: e.target.value }))} style={INPUT_STYLE} placeholder="Doctor name" /></div>
            </div>
          </section>
          <div style={{ height: 1, background: 'var(--color-border-tertiary, #e5e5e5)', margin: '12px 0' }} />
          <section>
            <div style={LABEL_STYLE}>What is the goal of this cycle?</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 7 }}>
              {pathCards.map((p) => (
                <div
                  key={p.value}
                  role="button"
                  tabIndex={0}
                  onClick={() => setForm((f) => ({ ...f, path: p.value }))}
                  onKeyDown={(e) => e.key === 'Enter' && setForm((f) => ({ ...f, path: p.value }))}
                  style={{
                    border: `0.5px solid ${form.path === p.value ? '#D4537E' : 'var(--color-border-secondary, #e0e0e0)'}`,
                    borderRadius: 10,
                    padding: '9px 11px',
                    cursor: 'pointer',
                    background: form.path === p.value ? '#FBEAF0' : 'transparent',
                  }}
                >
                  <div style={{ fontSize: 11, fontWeight: 500, color: '#2d1a22' }}>{p.title}</div>
                  <div style={{ fontSize: 9, color: 'var(--color-text-tertiary, #888)', lineHeight: 1.4 }}>{p.sub}</div>
                </div>
              ))}
            </div>
          </section>
          <div style={{ height: 1, background: 'var(--color-border-tertiary, #e5e5e5)', margin: '12px 0' }} />
          <section>
            <div style={LABEL_STYLE}>Retrieval</div>
            <div style={{ fontSize: 10, color: 'var(--color-text-tertiary, #888)', background: 'var(--color-background-secondary, #f8f8f8)', borderRadius: 6, padding: '7px 10px', marginBottom: 9 }}>
              Leave blank if retrieval hasn&apos;t happened yet — fill in after your appointment.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div><label style={LABEL_STYLE}>Retrieval date (est.)</label><input type="date" value={form.retrievalDate || form.estimatedRetrieval || ''} onChange={(e) => setForm((f) => ({ ...f, retrievalDate: e.target.value, estimatedRetrieval: e.target.value }))} style={INPUT_STYLE} /></div>
              <div><label style={LABEL_STYLE}>Follicles (last scan)</label><input type="number" min={0} placeholder="e.g. 12" value={form.follicleCount ?? ''} onChange={(e) => setForm((f) => ({ ...f, follicleCount: e.target.value ? Number(e.target.value) : null }))} style={INPUT_STYLE} /></div>
              <div><label style={LABEL_STYLE}>Eggs retrieved</label><input type="number" min={0} value={form.eggsRetrieved || ''} onChange={(e) => setForm((f) => ({ ...f, eggsRetrieved: e.target.value ? Number(e.target.value) : 0 }))} style={INPUT_STYLE} /></div>
              <div><label style={LABEL_STYLE}>Mature eggs / MII</label><input type="number" min={0} value={form.matureEggs || ''} onChange={(e) => setForm((f) => ({ ...f, matureEggs: e.target.value ? Number(e.target.value) : 0 }))} style={INPUT_STYLE} /></div>
              <div style={{ gridColumn: '1 / -1' }}><label style={LABEL_STYLE}>Stim medications</label><input value={form.stimMedications} onChange={(e) => setForm((f) => ({ ...f, stimMedications: e.target.value }))} style={INPUT_STYLE} placeholder="e.g. Gonal-F 150IU" /></div>
            </div>
          </section>
          {showFert && (
            <>
              <div style={{ height: 1, background: 'var(--color-border-tertiary, #e5e5e5)', margin: '12px 0' }} />
              <section>
                <div style={LABEL_STYLE}>Fertilization and embryo development</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <div><label style={LABEL_STYLE}>Fertilized day 1</label><input type="number" min={0} value={form.fertilized || ''} onChange={(e) => setForm((f) => ({ ...f, fertilized: e.target.value ? Number(e.target.value) : 0 }))} style={INPUT_STYLE} /></div>
                  <div><label style={LABEL_STYLE}>Blastocysts day 5/6</label><input type="number" min={0} value={form.blastocysts || ''} onChange={(e) => setForm((f) => ({ ...f, blastocysts: e.target.value ? Number(e.target.value) : 0 }))} style={INPUT_STYLE} /></div>
                  <div style={{ gridColumn: '1 / -1' }}><label style={LABEL_STYLE}>Embryo grades</label><input value={form.embryoGrades} onChange={(e) => setForm((f) => ({ ...f, embryoGrades: e.target.value }))} style={INPUT_STYLE} placeholder="e.g. 5AA, 4BB" /></div>
                  <div style={{ gridColumn: '1 / -1' }}><label style={LABEL_STYLE}>PGT testing</label><select value={form.pgtTesting} onChange={(e) => setForm((f) => ({ ...f, pgtTesting: e.target.value }))} style={INPUT_STYLE}>{PGT_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}</select></div>
                  {showPgtResults && <div style={{ gridColumn: '1 / -1' }}><label style={LABEL_STYLE}>PGT results</label><input value={form.pgtResults} onChange={(e) => setForm((f) => ({ ...f, pgtResults: e.target.value }))} style={INPUT_STYLE} placeholder="e.g. 2 euploid, 1 aneuploid" /></div>}
                </div>
              </section>
            </>
          )}
          {showTransfer && (
            <>
              <div style={{ height: 1, background: 'var(--color-border-tertiary, #e5e5e5)', margin: '12px 0' }} />
              <section>
                <div style={LABEL_STYLE}>Transfer</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <div style={{ gridColumn: '1 / -1' }}><label style={LABEL_STYLE}>Transfer type</label><select value={form.transferType || 'none'} onChange={(e) => setForm((f) => ({ ...f, transferType: e.target.value }))} style={INPUT_STYLE}>{TRANSFER_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select></div>
                  <div><label style={LABEL_STYLE}>Transfer date</label><input type="date" value={form.transferDate} onChange={(e) => setForm((f) => ({ ...f, transferDate: e.target.value }))} style={INPUT_STYLE} /></div>
                  <div style={{ gridColumn: '1 / -1' }}><label style={LABEL_STYLE}>Embryo transferred</label><input value={form.embryoTransferred} onChange={(e) => setForm((f) => ({ ...f, embryoTransferred: e.target.value }))} style={INPUT_STYLE} placeholder="e.g. 5AA, day 5" /></div>
                </div>
              </section>
              <div style={{ height: 1, background: 'var(--color-border-tertiary, #e5e5e5)', margin: '12px 0' }} />
              <section>
                <div style={LABEL_STYLE}>What happened?</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 7 }}>
                  {outcomeCards.map((o) => (
                    <div
                      key={o.value}
                      role="button"
                      tabIndex={0}
                      onClick={() => setForm((f) => ({ ...f, outcome: o.value, cancelled: o.value === 'cancelled' }))}
                      onKeyDown={(e) => e.key === 'Enter' && setForm((f) => ({ ...f, outcome: o.value }))}
                      style={{
                        border: `0.5px solid ${form.outcome === o.value ? '#D4537E' : 'var(--color-border-secondary, #e0e0e0)'}`,
                        borderRadius: 10,
                        padding: '9px 11px',
                        cursor: 'pointer',
                        background: form.outcome === o.value ? '#FBEAF0' : 'transparent',
                      }}
                    >
                      <div style={{ fontSize: 11, fontWeight: 500, color: '#2d1a22' }}>{o.title}</div>
                      <div style={{ fontSize: 9, color: 'var(--color-text-tertiary, #888)' }}>{o.sub}</div>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 8 }}><label style={LABEL_STYLE}>HCG level (optional)</label><input value={form.hcgLevel} onChange={(e) => setForm((f) => ({ ...f, hcgLevel: e.target.value }))} style={INPUT_STYLE} placeholder="e.g. 312" /></div>
              </section>
            </>
          )}
          <div style={{ height: 1, background: 'var(--color-border-tertiary, #e5e5e5)', margin: '12px 0' }} />
          <section>
            <div style={LABEL_STYLE}>Did the cycle get cancelled before retrieval?</div>
            <select value={form.cancelled ? form.cancelReason : 'No — cycle completed'} onChange={(e) => setForm((f) => ({ ...f, cancelled: e.target.value !== 'No — cycle completed', cancelReason: e.target.value }))} style={INPUT_STYLE}>
              {CANCEL_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </section>
          <div style={{ height: 1, background: 'var(--color-border-tertiary, #e5e5e5)', margin: '12px 0' }} />
          <section>
            <div style={LABEL_STYLE}>Notes</div>
            <textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} style={{ ...INPUT_STYLE, height: 68, resize: 'vertical' }} placeholder="Anything you want to remember — how you felt, what the doctor said, things to do differently next time..." />
          </section>
        </div>
        <div style={{ padding: '12px 18px', borderTop: '0.5px solid var(--color-border-tertiary, #e5e5e5)', display: 'flex', gap: 8 }}>
          <button type="button" onClick={onClose} style={{ background: 'transparent', border: '0.5px solid var(--color-border-secondary, #e0e0e0)', borderRadius: 999, padding: '8px 14px', fontSize: 12, cursor: 'pointer' }}>Cancel</button>
          <button type="button" onClick={onSave} style={{ flex: 1, background: '#D4537E', color: 'white', border: 'none', borderRadius: 999, padding: '8px 20px', fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>{editingCycle ? 'Save changes' : 'Save cycle'}</button>
        </div>
      </div>
    </div>
    </>
  );
}
